import React, { FC, useState, useEffect } from 'react';
import { Box, CircularProgress, ImageList, ImageListItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ScannerCase } from 'models/scanner-case';
import Cookies from 'js-cookie';
import { CONSTANTS } from 'config/constants';

type ThreeShapeJawPreviewProps = {
    scannerCase: ScannerCase;
    height?: number;
    cols?: number;
};
const ThreeShapeJawPreview: FC<ThreeShapeJawPreviewProps> = ({
    scannerCase,
    height,
    cols,
}: ThreeShapeJawPreviewProps) => {
    const { t: translate } = useTranslation(['three-shape', 'common']);
    const [objectUrls, setObjectUrls] = useState<Record<string, string>>({});
    const [previewImages, setPreviewImages] = useState<{ fileSourceId: string; fileName: string; url: string }[]>([]);

    const boxStyle = {
        height: height ? `${height}px` : '200px',
        border: '1px #bbb solid',
        background: 'rgba(250,250,250,1)',
    };

    useEffect(() => {
        if (!scannerCase?.sourceFiles) {
            setPreviewImages([]);
            return;
        }
        const images = scannerCase.sourceFiles
            .filter((f) => f.fileName.includes('AutoGeneratedImages.Models'))
            .map((image) => ({
                fileSourceId: image.fileSourceId,
                fileName: image.fileName,
                url: `${CONSTANTS.DOC_API_URI}/three-shape-download-proxy/cases/${scannerCase.caseId}/preview/${image.fileSourceId}`,
            }));
        setPreviewImages(images);
    }, [scannerCase]);

    useEffect(() => {
        let isMounted = true;
        Object.values(objectUrls).forEach((url) => URL.revokeObjectURL(url));
        setObjectUrls({});

        const fetchImages = async () => {
            const newObjectUrls: Record<string, string> = {};
            try {
                await Promise.all(
                    previewImages.map(async (img) => {
                        const res = await fetch(img.url, {
                            headers: {
                                authorization: Cookies.get(CONSTANTS.TOKEN_KEY) || '',
                            },
                        });
                        if (!res.ok) throw new Error('Failed to fetch image');
                        const blob = await res.blob();
                        newObjectUrls[img.fileSourceId] = URL.createObjectURL(blob);
                    }),
                );
                if (isMounted) setObjectUrls(newObjectUrls);
            } catch (e) {
                console.error(e);
                if (isMounted) {
                    setObjectUrls({});
                    setPreviewImages([]); // Hide the preview if any fetch fails
                }
            }
        };
        fetchImages();
        return () => {
            isMounted = false;
            // Revoke object URLs on unmount/cleanup
            Object.values(objectUrls).forEach((url) => URL.revokeObjectURL(url));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewImages]);

    const translationName = (fileName: string) => {
        if (fileName.includes('.UpperJaw')) return 'UPPERJAWSCAN';
        if (fileName.includes('.sideview')) return 'BITESCAN';
        if (fileName.includes('.LowerJaw')) return 'LOWERJAWSCAN';
        if (fileName.includes('.UpperImplantScan')) return 'UPPERIMPLANTSCAN';
        if (fileName.includes('.LowerImplantScan')) return 'LOWERIMPLANTSCAN';
    };

    return (
        <ImageList cols={cols || previewImages.length} sx={{ m: 0 }}>
            {previewImages.map((img) => (
                <ImageListItem key={img.fileSourceId}>
                    {!height || height > 160 ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 1,
                                left: 1,
                                padding: '4px 8px',
                                backgroundColor: 'rgba(255,255,255,0.5)',
                                color: '#000',
                            }}
                        >
                            <Typography sx={{ padding: 0 }} variant="body2">
                                {translate(`three-shape:${translationName(img.fileName)}`)}
                            </Typography>
                        </Box>
                    ) : undefined}
                    {objectUrls[img.fileSourceId] ? (
                        <img style={boxStyle} src={objectUrls[img.fileSourceId] || ''} />
                    ) : (
                        <Box sx={boxStyle} display="flex" justifyContent="center" alignItems="center">
                            <CircularProgress />
                        </Box>
                    )}
                </ImageListItem>
            ))}
        </ImageList>
    );
};

export default ThreeShapeJawPreview;
